"use client";

import { sendSlackMessage } from "@/app/serverActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createSupabaseClientForBrowser } from "@/utils/supabase/client";
import { domToPng } from "modern-screenshot";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useContext, useEffect, useRef, useState } from "react";
import { SupabaseAuthContext } from "./providers";
interface Message {
  text: string;
  sender: "user" | "founder";
}
export function FounderChat() {
  const {
    session,
    retrieveSession
  } = useContext(SupabaseAuthContext);
  const pathname = usePathname();
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const founderChatRef = useRef<HTMLDivElement>(null);
  const supabase = createSupabaseClientForBrowser();
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);
  const handleSendMessage = async () => {
    if (inputMessage.trim()) {
      const newMessage = {
        text: inputMessage,
        sender: "user" as const
      };
      setMessages(prevMessages => [...prevMessages, newMessage]);
      setInputMessage("");
      try {
        // If no session, sign in anonymously first
        let user_id = session?.user?.id;
        if (!user_id) {
          const session = await retrieveSession();
          user_id = session?.user?.id;
        }
        const {
          data: userInfo
        } = await supabase.from("user_info").select("*").eq("id", user_id!).single();
        const ipResponse = await fetch("https://ipapi.co/json/");
        const ipData = await ipResponse.json();

        // Reformatted Slack message with better hierarchy
        const slackMessage = `
*New Message from User*
>${inputMessage}

*User Details*
• ${userInfo?.first_name || ""} ${userInfo?.last_name || ""} (${userInfo?.email || "No email"})
• Network: ${ipData.org} / ${ipData.network} / ${ipData.ip}      
• IP Location: ${ipData.city}, ${ipData.region}, ${ipData.country_name}
• Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}
• User ID: \`${user_id}\`
• Page: ${window.location.href}
        `.trim();

        // Capture screenshot using modern-screenshot
        const screenshotDataUrl = await domToPng(document.documentElement, {
          filter: node => node !== founderChatRef.current
        });
        const screenshotBlob = await fetch(screenshotDataUrl).then(res => res.blob());

        // Upload screenshot to Supabase storage

        const fileName = `founder-chat-screenshot-${Date.now()}.png`;
        const filePath = `${user_id}/${fileName}`;
        const {
          data: uploadData,
          error: uploadError
        } = await supabase.storage.from("user-files").upload(filePath, screenshotBlob);
        if (uploadError) {
          throw uploadError;
        }

        // Create signed URL
        const {
          data: signedUrlData,
          error: signedUrlError
        } = await supabase.storage.from("user-files").createSignedUrl(filePath, 60 * 60 * 24 * 31); // 1 mo expiration

        if (signedUrlError) {
          throw signedUrlError;
        }

        // Send message and screenshot URL to Slack
        await sendSlackMessage("#web-chat-with-founders", slackMessage, signedUrlData.signedUrl);
        const isFirstUserMessage = messages.filter(msg => msg.sender === "user").length === 0;
        if (isFirstUserMessage) {
          setTimeout(() => {
            setMessages(prevMessages => [...prevMessages, {
              text: "Sorry, it looks like we're offline right now. We'll see your message as soon as we're back. Leave your email address if you'd like us to respond to you!",
              sender: "founder"
            }]);
          }, 3000);
        }
      } catch (error) {
        console.error("Error handling message:", error);
      }
    }
  };

  // Hide the chat if we're on "/dash" or any path beginning with "/sinatopia".
  if (pathname === "/dash" || pathname.startsWith("/sinatopia")) {
    return null;
  }
  return <div ref={founderChatRef} className="fixed bottom-4 right-4 z-50 will-change-transform" data-sentry-component="FounderChat" data-sentry-source-file="FounderChat.tsx">
      {!isExpanded ? <Button onClick={() => setIsExpanded(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full p-3 w-12 h-12 flex items-center justify-center border-2 border-white" aria-label="Chat with the founders">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </Button> : <div className="bg-card text-card-foreground rounded-lg p-4 shadow-xl w-80 border border-border">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold">Chat with the Founders</h2>
            <Button variant="ghost" size="sm" onClick={() => setIsExpanded(false)} className="text-muted-foreground">
              ✕
            </Button>
          </div>
          <div ref={chatContainerRef} className="h-64 overflow-y-auto mb-3 space-y-2">
            <div className="flex items-center space-x-2 mb-3">
              <Image src="/img/zain.png" alt="Zain Memon" width={40} height={40} className="rounded-full border" />
              <Image src="/img/sina4.png" alt="Sina Hartung" width={40} height={40} className="rounded-full border" />
              <p className="text-xs text-muted-foreground">
                We're Zain & Sina, founders of Eureka and fellow thyroid
                warriors. We'd love to hear your story and ideas on how we can
                make thyroid care better for you.
              </p>
            </div>
            {messages.map((msg, index) => <div key={index} className={`p-2 rounded-lg ${msg.sender === "user" ? "bg-primary text-primary-foreground ml-4" : "bg-gray-500 text-white mr-4"}`}>
                {msg.text}
              </div>)}
          </div>
          <div className="flex space-x-2">
            <Input value={inputMessage} onChange={e => setInputMessage(e.target.value)} placeholder="Type a message..." className="flex-grow" onKeyPress={e => e.key === "Enter" && handleSendMessage()} />
            <Button onClick={handleSendMessage}>Send</Button>
          </div>
        </div>}
    </div>;
}